/**
 * Dashboard filters, kept in the URL.
 *
 * The URL is the single source of truth rather than component state, so every
 * view is bookmarkable and shareable, the back button steps through filter
 * changes, and a reload lands you where you were. The stats strip links into
 * these same parameters -- clicking "Overdue" is just a link to `/?due=overdue`,
 * with no separate code path.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { ItemPriority, ItemQuery, ItemStatus } from '@/api/types'
import { PRIORITIES, STATUSES } from '@/api/types'

export type DueWindow = 'overdue' | 'today' | 'week' | 'month' | 'none'

export const DUE_WINDOWS: readonly DueWindow[] = [
  'overdue',
  'today',
  'week',
  'month',
  'none',
]

export const DUE_LABEL: Record<DueWindow, string> = {
  overdue: 'Overdue',
  today: 'Due today',
  week: 'This week',
  month: 'This month',
  none: 'No due date',
}

export interface Filters {
  q: string
  categoryId: number | null
  subcategoryId: number | null
  priority: ItemPriority[]
  status: ItemStatus[]
  tagIds: number[]
  due: DueWindow | null
}

function readNumber(value: string | null): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** Only values the API actually accepts survive; a hand-edited URL cannot
 *  produce a 422 from a filter it does not recognise. */
function readEnums<T extends string>(values: string[], allowed: readonly T[]): T[] {
  return values.filter((value): value is T => (allowed as readonly string[]).includes(value))
}

export function useFilters() {
  const [params, setParams] = useSearchParams()

  const filters = useMemo<Filters>(() => {
    const due = params.get('due')
    return {
      q: params.get('q') ?? '',
      categoryId: readNumber(params.get('category')),
      subcategoryId: readNumber(params.get('subcategory')),
      priority: readEnums(params.getAll('priority'), PRIORITIES),
      status: readEnums(params.getAll('status'), STATUSES),
      tagIds: params.getAll('tag').map(Number).filter(Number.isFinite),
      due: due && (DUE_WINDOWS as readonly string[]).includes(due) ? (due as DueWindow) : null,
    }
  }, [params])

  const active =
    filters.q !== '' ||
    filters.categoryId !== null ||
    filters.subcategoryId !== null ||
    filters.priority.length > 0 ||
    filters.status.length > 0 ||
    filters.tagIds.length > 0 ||
    filters.due !== null

  const update = useCallback(
    (patch: Partial<Filters>) => {
      const next = new URLSearchParams(params)

      const setOne = (key: string, value: string | number | null) => {
        next.delete(key)
        if (value !== null && value !== '') next.set(key, String(value))
      }
      const setMany = (key: string, values: (string | number)[]) => {
        next.delete(key)
        for (const value of values) next.append(key, String(value))
      }

      if ('q' in patch) setOne('q', patch.q ?? null)
      if ('categoryId' in patch) setOne('category', patch.categoryId ?? null)
      if ('subcategoryId' in patch) setOne('subcategory', patch.subcategoryId ?? null)
      if ('due' in patch) setOne('due', patch.due ?? null)
      if ('priority' in patch) setMany('priority', patch.priority ?? [])
      if ('status' in patch) setMany('status', patch.status ?? [])
      if ('tagIds' in patch) setMany('tag', patch.tagIds ?? [])

      // replace, not push: typing in the search box should not put one history
      // entry per keystroke between you and the previous page.
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const clear = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [
    setParams,
  ])

  /** Toggle one value of a multi-select filter. */
  const toggle = useCallback(
    <K extends 'priority' | 'status' | 'tagIds'>(key: K, value: Filters[K][number]) => {
      const current = filters[key] as Filters[K][number][]
      const next = current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value]
      update({ [key]: next } as unknown as Partial<Filters>)
    },
    [filters, update],
  )

  /** The filters as the API's query parameters. */
  const query = useMemo<ItemQuery>(() => {
    const built: ItemQuery = {}
    if (filters.q) built.q = filters.q
    if (filters.categoryId !== null) built.category_id = filters.categoryId
    if (filters.subcategoryId !== null) built.subcategory_id = filters.subcategoryId
    if (filters.priority.length) built.priority = filters.priority
    if (filters.status.length) built.status = filters.status
    if (filters.tagIds.length) built.tag_id = filters.tagIds
    if (filters.due) built.due = filters.due
    // Searching should reach the archive too: "where did I write that" is
    // usually about something already finished.
    if (filters.q || filters.status.some((s) => s === 'done' || s === 'dropped')) {
      built.include_closed = true
    }
    return built
  }, [filters])

  return { filters, active, update, toggle, clear, query }
}
